import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Alert, App, Button, Drawer, Form, Input, Select, Space } from 'antd';
import {
  ORG_UNIT_SUBTYPES,
  ORG_UNIT_TYPES,
  createOrgUnitSchema,
  updateOrgUnitSchema,
  type OrgUnitDto,
} from '@pams/shared';
import { OrgUnitPicker } from '@/components/OrgUnitPicker';
import type { ApiError } from '@/lib/api';
import { api } from '@/lib/api';
import { ORG_UNIT_SUBTYPE_LABEL, ORG_UNIT_TYPE_LABEL } from '@/lib/labels';
import { queryKeys } from '@/lib/queryKeys';
import { applyServerIssues, useZodForm } from '@/lib/zodForm';

/**
 * Create/edit an org unit.
 *
 * The `{ open, onClose, onSuccess, payload }` modal convention from
 * PAMS_CONVENTIONS §1, and every validation rule comes from the shared schema.
 *
 * NOTE the schema switch: create takes `parentId`, edit does not. Moving a node is
 * `MoveUnitModal`, because reparenting rewrites every descendant's ancestors and
 * therefore changes who can see whom — that is not the same kind of act as renaming.
 */
export const OrgUnitDrawer = ({
  open,
  mode,
  unit,
  parent,
  readOnly,
  onClose,
}: {
  open: boolean;
  mode: 'create' | 'edit';
  unit?: OrgUnitDto;
  parent?: OrgUnitDto;
  readOnly: boolean;
  onClose: () => void;
}) => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();
  const schema = mode === 'create' ? createOrgUnitSchema : updateOrgUnitSchema;
  const { form, field, submit, formErrors, setFormErrors } = useZodForm(schema);

  const save = useMutation({
    mutationFn: (values: Record<string, unknown>) =>
      mode === 'create'
        ? api.post<OrgUnitDto>('/org-units', values)
        : api.patch<OrgUnitDto>(`/org-units/${unit?.id ?? ''}`, values),
    onSuccess: () => {
      void message.success(mode === 'create' ? 'Org unit created' : 'Org unit updated');
      void queryClient.invalidateQueries({ queryKey: queryKeys.orgUnits.all });
      onClose();
    },
    onError: (error: ApiError) => {
      // A 422 from the server is mapped back onto the individual fields, so the two
      // validation layers agree about which input is at fault.
      setFormErrors(applyServerIssues(form, error.details).concat(error.details ? [] : [error.message]));
    },
  });

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={480}
      title={mode === 'create' ? 'New org unit' : `Edit ${unit?.name ?? ''}`}
      extra={
        <Space>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" disabled={readOnly} loading={save.isPending} onClick={() => form.submit()}>
            Save
          </Button>
        </Space>
      }
    >
      {formErrors.length > 0 && <Alert type="error" showIcon className="mb-4" message={formErrors.join(' ')} />}

      <Form
        form={form}
        layout="vertical"
        /* AntD cascades `disabled` to every control inside, so a read-only role gets a
           detail panel rather than an editable form even on a deep link. */
        disabled={readOnly}
        initialValues={
          mode === 'create'
            ? { type: 'UNIT', subType: 'OTHER', parentId: parent?.id ?? null }
            : { name: unit?.name, shortCode: unit?.shortCode, type: unit?.type, subType: unit?.subType }
        }
        onFinish={(values) => {
          const parsed = submit(values);
          if (parsed.ok) save.mutate(parsed.data);
        }}
      >
        <Form.Item {...field('name')} label="Name">
          <Input placeholder="Tollygunge Police Station" />
        </Form.Item>

        <Form.Item {...field('shortCode')} label="Short code" tooltip="Upper-cased and unique across the tree.">
          <Input placeholder="TOLLYPS" />
        </Form.Item>

        <Form.Item {...field('type')} label="Type">
          <Select options={ORG_UNIT_TYPES.map((value) => ({ value, label: ORG_UNIT_TYPE_LABEL[value] }))} />
        </Form.Item>

        <Form.Item
          {...field('subType')}
          label="Sub-type"
          tooltip="So non-PS units fit without a schema change (PA_REQUIREMENTS §4.2)."
        >
          {/*
            ⚠️ [amended WINGS.1] LABELLED, NOT RAW — BOTH SELECTS, because the argument does
            not stop at one of them. They rendered the enums verbatim, so this drawer read
            `WING` and `DIVISION` while the table's own Type column two panes over read `Wing`
            and `Division` — one screen, two spellings of each value. Both label maps are
            `Record<Enum, string>`, so a member added later is a compile error here rather than
            a SCREAMING_SNAKE leak into the UI.
          */}
          <Select options={ORG_UNIT_SUBTYPES.map((value) => ({ value, label: ORG_UNIT_SUBTYPE_LABEL[value] }))} />
        </Form.Item>

        {mode === 'create' && (
          <Form.Item
            {...field('parentId')}
            label="Parent unit"
            tooltip="Leave empty only when creating a root commissionerate — which requires global scope."
          >
            <OrgUnitPicker allowClear placeholder="Select the parent unit" />
          </Form.Item>
        )}
      </Form>
    </Drawer>
  );
};
